<script lang="ts" setup>
import { ref, provide, inject, watch, onMounted } from 'vue';
import HotspotList from '@/components/Hotspots/HotspotList.vue';
import HotspotDraggable from '@/components/Hotspots/HotspotDraggable.vue';
import HotspotEdit from '@/components/Hotspots/HotspotEdit.vue';

import type { Ref } from 'vue';
import type { Nullable } from '@immersive-tech/viewer-engine';
import type { IManifest, IManifestHotspot } from '@/types/IManifest';

interface IManifestHotspotArrayValue extends IManifestHotspot {
  key: string;
}

const manifest = inject('manifest') as Ref<IManifest>;
const activeHotspot = ref<Nullable<string>>(null);
const hotspotsArray = ref<IManifestHotspotArrayValue[]>([]);

const generateHotspotsArrayFromManifest = () => {
  hotspotsArray.value = Object.entries(manifest.value.hotspots)
    .map(([key, value]) => ({ ...value, key }))
    .sort((hotspotLeft, hotspotRight) => {
      if (hotspotLeft.order < hotspotRight.order) return -1;
      else if (hotspotLeft.order > hotspotRight.order) return 1;

      return 0;
    });
};

const deleteHotspot = (identifier: string) => {
  const hotspotUpdate = hotspotsArray.value.findIndex((hotspot) => hotspot.hotspotName === identifier);

  if (hotspotUpdate !== -1) {
    hotspotsArray.value.splice(hotspotUpdate, 1);
  }
};

const mountHandler = () => {
  generateHotspotsArrayFromManifest();
};

const manifestWatcher = () => {
  generateHotspotsArrayFromManifest();
};

const hotspotsArrayWatcher = (newValue: IManifestHotspotArrayValue[]) => {
  for (let hotspotIndex = 0; hotspotIndex < newValue.length; ++hotspotIndex) {
    const singleNewHotspot = newValue[hotspotIndex];

    manifest.value.hotspots[singleNewHotspot.key].order = Number(hotspotIndex);
  }
};

const manifestHotspotsWatcher = (newValue: string[]) => {
  const newHotspots = newValue;
  const oldHotspots = Object.keys(hotspotsArray.value);

  const change = newHotspots.find((hotspot) => !oldHotspots.includes(hotspot));

  if (change) {
    generateHotspotsArrayFromManifest();
  }
};

onMounted(mountHandler);

watch(hotspotsArray, hotspotsArrayWatcher);
watch(manifest, manifestWatcher);
watch(() => Object.keys(manifest.value.hotspots), manifestHotspotsWatcher, { deep: true });

provide('activeHotspot', activeHotspot);
provide('hotspotsArray', hotspotsArray);
</script>

<template>
  <HotspotList />
  <HotspotEdit v-if="activeHotspot" :identifier="activeHotspot" @delete="deleteHotspot" />
  <HotspotDraggable />
</template>
